import { useEffect, useState } from "react";
import { Film, Loader2, Music2, Download, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, downloadUrl } from "@/lib/api";

const STYLES = [["epica", "Épica"], ["suspenso", "Suspenso"], ["emotiva", "Emotiva"], ["urbana", "Urbana"]];

const pill = (on) => `px-3 py-1.5 rounded-full text-sm border transition-colors ${on
  ? "border-[#FF334B] bg-[#FF334B]/15 text-white" : "border-white/12 text-[#94A3B8] hover:text-white"}`;

const showErr = (e, fallback) => {
  const d = e?.response?.data?.detail || fallback;
  if (e?.response?.status === 402) toast.error(d, { action: { label: "Recargar", onClick: () => window.location.assign("/precios") } });
  else toast.error(d);
};

// Vista previa de costo OBLIGATORIA + confirmación del tráiler vertical de 30 s.
export const TrailerDialog = ({ project, onClose, onStarted }) => {
  const [music, setMusic] = useState(false);
  const [style, setStyle] = useState("epica");
  const [cost, setCost] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setCost(null);
    api.post(`/projects/${project.id}/trailer/preview`, { music, style })
      .then((r) => alive && setCost(r.data))
      .catch((e) => { showErr(e, "No se pudo calcular el costo"); onClose(); });
    return () => { alive = false; };
  }, [project.id, music, style]);  // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/trailer?confirm=true`, { music, style });
      toast.success("Creando tu tráiler vertical de 30 s…");
      onStarted?.();
      onClose();
    } catch (e) { showErr(e, "No se pudo crear el tráiler"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="trailer-modal" onClick={onClose}>
      <div className="mfp-card max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2"><Film className="w-5 h-5 text-[#FF334B]" /><h3 className="font-head text-xl font-bold">Tráiler vertical · 30 s</h3></div>
        <p className="text-sm text-[#94A3B8]">Título de la serie y el capítulo al inicio, los mejores momentos del capítulo en pantalla completa 9:16 y cierre «Próximamente · Mira el capítulo completo». Listo para Reels y TikTok.</p>
        <label className="flex items-center justify-between gap-3 rounded-xl border border-white/10 p-3 cursor-pointer">
          <span className="inline-flex items-center gap-2 text-sm"><Music2 className="w-4 h-4 text-[#FF9F2E]" /> Música nueva con IA</span>
          <input type="checkbox" checked={music} onChange={(e) => setMusic(e.target.checked)} data-testid="trailer-music-toggle" className="w-5 h-5 accent-[#FF334B]" />
        </label>
        {music && (
          <div className="flex flex-wrap gap-2" data-testid="trailer-style-list">
            {STYLES.map(([k, l]) => <button key={k} type="button" onClick={() => setStyle(k)} className={pill(style === k)} data-testid={`trailer-style-${k}`}>{l}</button>)}
          </div>
        )}
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-1.5 text-sm" data-testid="trailer-cost">
          {!cost ? <div className="inline-flex items-center gap-2 text-[#64748B]"><Loader2 className="w-4 h-4 spin" /> Calculando…</div> : (
            <>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Formato</span><strong>9:16 · {cost.seconds} s · {cost.shots} momentos</strong></div>
              <div className="flex justify-between"><span className="text-[#94A3B8]">Música</span><strong>{!cost.music ? "Audio original del capítulo" : cost.music_reused ? "IA (ya pagada, se reutiliza)" : cost.model}</strong></div>
              <div className="flex justify-between border-t border-white/10 pt-1.5"><span className="text-[#94A3B8]">Costo real en Fal</span><strong className="text-[#FF334B]" data-testid="trailer-cost-usd">{cost.total_usd > 0 ? `$${cost.total_usd} USD` : "GRATIS ($0)"}</strong></div>
              {cost.total_credits > 0 && <div className="flex justify-between"><span className="text-[#94A3B8]">Créditos</span><strong data-testid="trailer-cost-credits">{cost.total_credits} cr</strong></div>}
            </>
          )}
        </div>
        <div className="flex gap-3">
          <button data-testid="trailer-cancel" onClick={onClose} className="flex-1 py-3 rounded-full border border-white/15 text-[#94A3B8] hover:text-white transition-colors">Cancelar</button>
          <button data-testid="trailer-confirm" onClick={confirm} disabled={busy || !cost}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-3 rounded-full transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-5 h-5 spin" /> : <Film className="w-5 h-5" />} {cost?.total_usd > 0 ? "Confirmar y crear" : "Crear gratis"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Estado / resultado del tráiler en una tarjeta de capítulo o proyecto.
export const TrailerResult = ({ project, filename }) => {
  const r = project.result || {};
  if (project.busy === "trailer")
    return <p className="text-xs text-[#FF9F2E] inline-flex items-center gap-1.5" data-testid={`trailer-busy-${project.id}`}><Loader2 className="w-3.5 h-3.5 spin" /> Creando tráiler 9:16…</p>;
  return (
    <>
      {project.busy_error && /tráiler/i.test(project.busy_error) && (
        <p className="text-xs text-red-300 flex items-start gap-1.5" data-testid={`trailer-error-${project.id}`}><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{project.busy_error}</p>
      )}
      {r.trailer_path && (
        <div className="flex items-start gap-3" data-testid={`trailer-result-${project.id}`}>
          <video src={mediaUrl(r.trailer_path)} controls playsInline className="w-28 aspect-[9/16] rounded-lg bg-black object-cover border border-white/10" data-testid={`trailer-video-${project.id}`} />
          <div className="space-y-2">
            <p className="text-xs text-[#94A3B8]">Tráiler 30 s · 9:16 (Reels / TikTok)</p>
            <a href={downloadUrl(r.trailer_path, `${filename || "trailer"}-trailer`)} data-testid={`trailer-download-${project.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-white text-black hover:opacity-90 transition-opacity">
              <Download className="w-3.5 h-3.5" /> Descargar tráiler
            </a>
          </div>
        </div>
      )}
    </>
  );
};
